import { useCallback } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { NodeProps } from "@xyflow/react"

import { Type, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { NodeEditDialog } from "./node-edit-dialog"
import { ReferenceNodesPicker } from "./reference-nodes-picker"
import { TemplateField } from "./template-field"

/**
 * Format node: wraps the incoming output in a fixed template for consistent
 * formatting across runs. {input} is the upstream output; {variable_name}
 * substitutes a workflow variable. Empty template passes the input through.
 */
export function FormatNode({ id, data, selected }: NodeProps) {
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)

  // Loop tokens now evaluate inside loops, so re-enable loop suggestions when
  // this Format node is a child of a loopContainer (parentId points to one).
  const isInLoop = useWorkflowStore((state) => {
    const self = state.nodes.find((n) => n.id === id)
    if (!self?.parentId) return false
    return state.nodes.some((n) => n.id === self.parentId && n.type === "loopContainer")
  })

  const handleShowOutputChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showOutput: checked })
    },
    [id, updateNodeData]
  )

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  const template = (data.template as string) || ""
  const hasOutput = isCompleted && !!output
  const renderEditor = (isModal = false) => (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <Label className="text-xs">Template</Label>
        <TemplateField
          multiline
          allowLoopTokens={isInLoop}
          className={cn("pr-8", isModal ? "min-h-[360px]" : "min-h-20")}
          placeholder={"## Result\n\n{input}"}
          value={template}
          onChange={(value) => updateNodeData(id, { template: value })}
        />
        <p className="text-muted-foreground text-[10px]">
          Use {"{input}"} for the incoming text and {"{variable}"} for workflow variables.
        </p>
      </div>
      <ReferenceNodesPicker
        currentNodeId={id}
        selectedNodeIds={(data.nodeReferences as string[] | undefined) ?? []}
        onChange={(ids) => updateNodeData(id, { nodeReferences: ids })}
      />
      <div className="flex items-center gap-2 border-t pt-2">
        <Checkbox
          checked={!!data.showOutput}
          className="nodrag"
          id={`${id}-showOutput${isModal ? "-modal" : ""}`}
          onCheckedChange={handleShowOutputChange}
        />
        <Label
          className="cursor-pointer text-xs font-normal"
          htmlFor={`${id}-showOutput${isModal ? "-modal" : ""}`}
        >
          Show Output
        </Label>
      </div>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[220px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[2])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
            <Type className="size-4" />
            Format
            <NodeEditDialog
              allowLoopTokens={isInLoop}
              description="Edit this format template in a larger workspace."
              title="Format settings"
            >
              {renderEditor(true)}
            </NodeEditDialog>
          </CardTitle>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-3">
          {renderEditor()}
          <Handle position={Position.Left} type="target" />
          <Handle position={Position.Right} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
